import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { buildUpcomingDays, toISODate, mondayIndex, WEEKDAYS_FR, MONTHS_FR, formatDateFR } from "@/lib/dates";
import { Clock, Calendar as CalIcon, Car, CheckCircle2, ChevronLeft, Loader2, Copy, Snowflake } from "lucide-react";
import { toast } from "sonner";
import Combobox from "@/components/Combobox";
import AccessInfo from "@/components/AccessInfo";
import { CAR_BRANDS, CAR_BRAND_NAMES, VEHICLE_YEARS, brandLogoUrl } from "@/data/vehicles";

const inputCls =
  "w-full bg-[#050810] border border-[#1A2642] text-white rounded-md px-4 py-3 text-sm placeholder:text-[#475569] focus:outline-none focus:ring-2 focus:ring-[#00B4D8] focus:border-transparent transition";
const labelCls = "block text-[11px] font-bold uppercase tracking-[0.18em] text-[#94A3B8] mb-2";

export default function BookingFlow({ service, settings, customer, onReset }) {
  const [step, setStep] = useState("date"); // date | info | done
  const [days, setDays] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [form, setForm] = useState({
    customer_name: "", customer_email: "", customer_phone: "",
    marque: "", modele: "", annee: "", immatriculation: "", notes: "",
  });

  useEffect(() => {
    if (!settings) return;
    const all = buildUpcomingDays(28);
    const open = all.filter((d) => {
      const iso = toISODate(d);
      const cfg = settings.weekly_hours?.[String(mondayIndex(d))];
      return cfg && cfg.open && !(settings.closed_dates || []).includes(iso);
    });
    setDays(open.slice(0, 14));
  }, [settings]);

  // reset on service change
  useEffect(() => {
    setStep("date"); setSelectedDate(null); setSelectedSlot(null); setSlots([]); setResult(null);
  }, [service]);

  // prefill contact fields from logged-in customer
  useEffect(() => {
    if (customer) {
      setForm((f) => ({
        ...f,
        customer_name: f.customer_name || customer.name || customer.first_name || "",
        customer_email: f.customer_email || customer.email || "",
        customer_phone: f.customer_phone || customer.phone || "",
      }));
    }
  }, [customer]);

  const loadSlots = async (iso) => {
    setSelectedDate(iso); setSelectedSlot(null); setSlots([]); setSlotsLoading(true);
    try {
      const { data } = await api.get(`/availability`, { params: { service_id: service.id, date: iso } });
      setSlots(data.slots);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSlotsLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.marque || !form.modele || !form.annee) {
      toast.error("Merci de renseigner la marque, le modèle et l'année du véhicule.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post(`/bookings`, {
        service_id: service.id,
        date: selectedDate,
        start_time: selectedSlot,
        customer_name: form.customer_name,
        customer_email: form.customer_email,
        customer_phone: form.customer_phone,
        vehicle: { marque: form.marque, modele: form.modele, annee: form.annee, immatriculation: form.immatriculation },
        notes: form.notes,
      });
      setResult(data);
      setStep("done");
      toast.success("Réservation confirmée !");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (!service) {
    return (
      <div className="glass border border-[#1A2642] rounded-lg p-10 text-center" data-testid="booking-empty">
        <Snowflake className="w-10 h-10 text-[#00B4D8] mx-auto mb-4" />
        <p className="text-[#94A3B8]">Sélectionnez une prestation ci-dessus pour voir les créneaux disponibles.</p>
      </div>
    );
  }

  const stepIndex = step === "date" ? 1 : step === "info" ? 2 : 3;
  const filledCount =
    ["customer_name", "customer_email", "customer_phone", "marque", "modele", "annee"].filter((k) => form[k]).length;
  let progress;
  if (step === "done") progress = 100;
  else if (step === "info") progress = 60 + Math.round((filledCount / 6) * 35);
  else progress = 15 + (selectedDate ? 20 : 0) + (selectedSlot ? 25 : 0);

  const STEPS = [
    { n: 1, label: "Créneau", icon: CalIcon },
    { n: 2, label: "Véhicule", icon: Car },
    { n: 3, label: "Confirmé", icon: CheckCircle2 },
  ];

  return (
    <div className="glass border border-[#1A2642] rounded-lg overflow-hidden" data-testid="booking-flow">
      {/* Service header */}
      <div className="flex items-center justify-between gap-4 p-5 md:p-6 border-b border-[#1A2642] bg-[#00B4D8]/5">
        <div className="min-w-0">
          <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#00B4D8]">Prestation choisie</p>
          <h3 className="text-lg md:text-xl font-bold text-white truncate">{service.name}</h3>
          <div className="flex items-center gap-4 mt-1 text-sm text-[#94A3B8]">
            <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" />{service.duration_minutes} min</span>
            <span className="text-[#FFD60A] font-bold text-base">{service.price} €</span>
          </div>
        </div>
        <button onClick={onReset} data-testid="change-service-btn"
          className="shrink-0 text-xs font-semibold text-[#94A3B8] hover:text-white border border-[#1A2642] hover:border-[#00B4D8] rounded-full px-3 py-2 transition">
          Changer
        </button>
      </div>

      {/* Progress gauge + steps */}
      <div className="px-5 md:px-6 pt-5" data-testid="booking-progress">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            {STEPS.map((s) => {
              const active = stepIndex === s.n;
              const done = stepIndex > s.n;
              const Icon = s.icon;
              return (
                <div key={s.n} className="flex items-center gap-1.5">
                  <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold border transition ${done ? "bg-[#06D6A0] border-[#06D6A0] text-[#050810]" : active ? "bg-[#00B4D8] border-[#00B4D8] text-[#050810]" : "border-[#1A2642] text-[#475569]"}`}>
                    {done ? <CheckCircle2 className="w-3.5 h-3.5" /> : s.n}
                  </span>
                  <span className={`text-xs font-semibold hidden sm:inline ${active || done ? "text-white" : "text-[#475569]"}`}>{s.label}</span>
                </div>
              );
            })}
          </div>
          <span className="text-sm font-black text-[#FFD60A] tabular-nums" data-testid="progress-pct">{progress}%</span>
        </div>
        <div className="h-2.5 w-full rounded-full bg-[#050810] border border-[#1A2642] overflow-hidden">
          <div className="h-full rounded-full bg-gradient-to-r from-[#00B4D8] to-[#90E0EF] transition-all duration-500 ease-out shadow-[0_0_12px_rgba(0,180,216,0.6)]"
            style={{ width: `${progress}%` }} />
        </div>
      </div>

      {step === "done" && result ? (
        <div className="p-6 md:p-10 text-center animate-float-up" data-testid="booking-confirmation">
          <CheckCircle2 className="w-16 h-16 text-[#06D6A0] mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-white mb-2">C'est réservé !</h3>
          <p className="text-[#94A3B8] max-w-md mx-auto">
            Un email de confirmation a été envoyé à <span className="text-white font-semibold">{form.customer_email}</span>.
            Vous pouvez gérer, décaler ou annuler votre rendez-vous via le lien ci-dessous.
          </p>
          <div className="mt-6 bg-[#050810] border border-[#1A2642] rounded-md p-4 text-left max-w-md mx-auto">
            <div className="flex justify-between py-1.5 text-sm"><span className="text-[#94A3B8]">Date</span><span className="text-white font-medium">{formatDateFR(result.booking.date)}</span></div>
            <div className="flex justify-between py-1.5 text-sm"><span className="text-[#94A3B8]">Horaire</span><span className="text-white font-medium">{result.booking.start_time} - {result.booking.end_time}</span></div>
            <div className="flex justify-between py-1.5 text-sm"><span className="text-[#94A3B8]">À régler sur place</span><span className="text-[#FFD60A] font-bold">{result.booking.service_price} €</span></div>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 justify-center mt-6">
            <a href={`/gerer/${result.manage_token}`} data-testid="goto-manage-btn"
              className="bg-[#FFD60A] text-[#050810] font-bold rounded-full px-6 py-3 hover:bg-[#FFEA00] transition">
              Gérer ma réservation
            </a>
            <button onClick={() => { navigator.clipboard.writeText(window.location.origin + `/gerer/${result.manage_token}`); toast.success("Lien copié"); }}
              className="flex items-center justify-center gap-2 border border-[#1A2642] text-white rounded-full px-6 py-3 hover:border-[#00B4D8] transition">
              <Copy className="w-4 h-4" /> Copier le lien
            </button>
          </div>

          <div className="mt-8 pt-6 border-t border-[#1A2642] max-w-md mx-auto">
            <AccessInfo date={result.booking.date} startTime={result.booking.start_time} endTime={result.booking.end_time} />
          </div>
        </div>
      ) : step === "date" ? (
        <div className="p-5 md:p-6" data-testid="booking-step-date">
          <p className={labelCls}><CalIcon className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />Choisissez une date</p>
          <div className="flex gap-2 overflow-x-auto pb-3 -mx-1 px-1">
            {days.map((d) => {
              const iso = toISODate(d);
              const active = selectedDate === iso;
              return (
                <button key={iso} onClick={() => loadSlots(iso)} data-testid={`day-${iso}`}
                  className={`shrink-0 w-16 py-3 rounded-md border text-center transition ${active ? "bg-[#00B4D8] border-[#00B4D8] text-[#050810]" : "bg-[#050810] border-[#1A2642] text-white hover:border-[#00B4D8]"}`}>
                  <div className="text-[10px] font-bold uppercase opacity-80">{WEEKDAYS_FR[mondayIndex(d)]}</div>
                  <div className="text-xl font-bold leading-tight">{d.getDate()}</div>
                  <div className="text-[10px] opacity-80">{MONTHS_FR[d.getMonth()]}</div>
                </button>
              );
            })}
          </div>

          {selectedDate && (
            <div className="mt-5">
              <p className={labelCls}><Clock className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />Créneaux disponibles</p>
              {slotsLoading ? (
                <div className="py-8 text-center text-[#94A3B8]"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
              ) : slots.length === 0 ? (
                <p className="text-sm text-[#94A3B8] py-4">Aucun créneau disponible ce jour. Choisissez une autre date.</p>
              ) : (
                <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-6 gap-2">
                  {slots.map((s) => (
                    <button key={s} onClick={() => setSelectedSlot(s)} data-testid={`slot-${s}`}
                      className={`py-2.5 rounded-md border text-sm font-semibold transition ${selectedSlot === s ? "bg-[#FFD60A] border-[#FFD60A] text-[#050810]" : "bg-[#050810] border-[#1A2642] text-white hover:border-[#00B4D8]"}`}>
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <button disabled={!selectedSlot} onClick={() => setStep("info")} data-testid="to-info-btn"
            className="w-full mt-6 bg-[#00B4D8] text-[#050810] font-bold rounded-full px-6 py-3.5 hover:bg-[#90E0EF] disabled:opacity-40 disabled:cursor-not-allowed transition">
            Continuer
          </button>
        </div>
      ) : (
        <form onSubmit={submit} className="p-5 md:p-6 space-y-5" data-testid="booking-step-info">
          <button type="button" onClick={() => setStep("date")} className="flex items-center gap-1 text-sm text-[#94A3B8] hover:text-white transition">
            <ChevronLeft className="w-4 h-4" /> Retour
          </button>
          <div className="bg-[#050810] border border-[#1A2642] rounded-md p-3 text-sm text-[#94A3B8]">
            <CalIcon className="w-4 h-4 inline mr-1.5 text-[#00B4D8] -mt-0.5" />
            {formatDateFR(selectedDate)} à <span className="text-white font-semibold">{selectedSlot}</span>
          </div>

          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#00B4D8] mb-3">Vos coordonnées</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2"><label className={labelCls}>Nom complet *</label>
                <input required className={inputCls} data-testid="input-name" placeholder="Jean Dupont"
                  value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} /></div>
              <div><label className={labelCls}>Email *</label>
                <input required type="email" className={inputCls} data-testid="input-email" placeholder="jean@email.fr"
                  value={form.customer_email} onChange={(e) => setForm({ ...form, customer_email: e.target.value })} /></div>
              <div><label className={labelCls}>Téléphone *</label>
                <input required className={inputCls} data-testid="input-phone" placeholder="06 12 34 56 78"
                  value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value })} /></div>
            </div>
          </div>

          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#00B4D8] mb-3"><Car className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />Votre véhicule</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className={labelCls}>Marque *</label>
                <Combobox testid="select-marque" placeholder="Choisir une marque" value={form.marque}
                  options={CAR_BRAND_NAMES} optionIcon={brandLogoUrl}
                  onChange={(v) => setForm({ ...form, marque: v, modele: "" })} /></div>
              <div><label className={labelCls}>Modèle *</label>
                <Combobox testid="select-modele" placeholder={form.marque ? "Choisir un modèle" : "Sélectionnez d'abord la marque"}
                  disabled={!form.marque} value={form.modele}
                  options={CAR_BRANDS[form.marque] || []}
                  onChange={(v) => setForm({ ...form, modele: v })} /></div>
              <div><label className={labelCls}>Année *</label>
                <Combobox testid="select-annee" placeholder="Choisir l'année" value={form.annee}
                  options={VEHICLE_YEARS} allowCustom={false}
                  onChange={(v) => setForm({ ...form, annee: v })} /></div>
              <div><label className={labelCls}>Immatriculation</label>
                <input className={inputCls} data-testid="input-immat" placeholder="AB-123-CD"
                  value={form.immatriculation} onChange={(e) => setForm({ ...form, immatriculation: e.target.value })} /></div>
            </div>
          </div>

          <div><label className={labelCls}>Remarques (optionnel)</label>
            <textarea className={inputCls} rows={2} data-testid="input-notes" placeholder="Précisions sur votre demande…"
              value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>

          <button type="submit" disabled={submitting} data-testid="submit-booking"
            className="w-full bg-[#FFD60A] text-[#050810] font-black text-base rounded-full px-6 py-4 hover:bg-[#FFEA00] disabled:opacity-50 transition flex items-center justify-center gap-2 animate-pulse-glow">
            {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : null}
            Confirmer ma réservation · {service.price} €
          </button>
          <p className="text-center text-xs text-[#64748B]">Paiement sur place · Aucune carte requise</p>
        </form>
      )}
    </div>
  );
}
